import { scopedApi, type ApiScope } from "@/features/utils/api-scope";
import { api, fetchServer } from "@/features/utils/fetch-server";
import type {
  AdminSendAs,
  SearchSessionsOptions,
  SearchSessionsResult,
  Session,
  SessionListItem,
  SessionStats,
} from "../types";

/** Who a test conversation pretends to come from (« Simuler un contact »). */
export type SimulatedContact = { phone_number?: string; email?: string };

const base = (clientId: string) => `/api/sessions/v1/${clientId}`;

export class SessionsAPI {
  /** @deprecated use {@link SessionsAPI.search} instead. */
  static list = async (
    clientId: string,
    options?: {
      customer_id?: string;
      agent_id?: string;
      state?: "open" | "closed";
    },
  ): Promise<SessionListItem[]> => {
    const params = new URLSearchParams();
    if (options?.customer_id) params.append("customer_id", options.customer_id);
    if (options?.agent_id) params.append("agent_id", options.agent_id);
    if (options?.state) params.append("state", options.state);

    const queryString = params.toString();
    return api.get(
      `${base(clientId)}/sessions${queryString ? `?${queryString}` : ""}`,
    );
  };

  /**
   * `scope` on the three reads the client's file in the platform back-office
   * shows (search, stats, one conversation): `staff` reads them through the
   * staff mount (`/staff/:clientId/…`, read only).
   */
  static search = async (
    clientId: string,
    options?: SearchSessionsOptions,
    scope: ApiScope = "tenant",
  ): Promise<SearchSessionsResult> => {
    const params = new URLSearchParams();
    if (options?.customer_id) params.append("customer_id", options.customer_id);
    if (options?.agent_id) params.append("agent_id", options.agent_id);
    if (options?.state) params.append("state", options.state);
    if (options?.type) params.append("type", options.type);
    if (options?.medium) params.append("medium", options.medium);
    if (options?.tags) params.append("tags", options.tags);
    if (options?.ids) params.append("ids", options.ids);
    if (options?.category) params.append("category", options.category);
    if (options?.search) params.append("search", options.search);
    if (options?.date_from) params.append("date_from", options.date_from);
    if (options?.date_to) params.append("date_to", options.date_to);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());
    if (options?.sort_by) params.append("sort_by", options.sort_by);
    if (options?.sort_order) params.append("sort_order", options.sort_order);

    return api.get(
      `${scopedApi(scope, "sessions", clientId)}/sessions/search?${params.toString()}`,
    );
  };

  static stats = async (
    clientId: string,
    options?: { date_from?: string; date_to?: string; timezone?: string },
    scope: ApiScope = "tenant",
  ): Promise<SessionStats> => {
    const params = new URLSearchParams();
    if (options?.date_from) params.append("date_from", options.date_from);
    if (options?.date_to) params.append("date_to", options.date_to);
    if (options?.timezone) params.append("timezone", options.timezone);

    const queryString = params.toString();
    return api.get(
      `${scopedApi(scope, "sessions", clientId)}/stats${queryString ? `?${queryString}` : ""}`,
    );
  };

  static get = async (
    clientId: string,
    sessionId: string,
    scope: ApiScope = "tenant",
  ): Promise<Session> =>
    api.get(`${scopedApi(scope, "sessions", clientId)}/sessions/${sessionId}`);

  /**
   * A web conversation with one given agent, for a member trying it from the
   * back-office (« Tester <agent> »). It opens on the agent's greeting, which
   * arrives over the socket, so the reply is the still-empty session.
   */
  static startAgentTest = async (
    clientId: string,
    agentId: string,
    simulate?: SimulatedContact,
  ): Promise<Session> =>
    api.post(`${base(clientId)}/sessions/test`, {
      agent_id: agentId,
      simulate,
    });

  static sendMessage = async (
    clientId: string,
    sessionId: string,
    message: string,
  ): Promise<Session> =>
    api.post(`${base(clientId)}/sessions/${sessionId}/send`, { message });

  /**
   * A HeyUp administrator writing into a conversation from its history
   * (SYSADMIN-only server-side): an instruction to the agent the customer never
   * sees, a line as if the customer typed it, or an SMS in the agent's name.
   */
  static adminSend = async (
    clientId: string,
    sessionId: string,
    message: string,
    as: AdminSendAs,
  ): Promise<Session> =>
    api.post(`${base(clientId)}/sessions/${sessionId}/admin-send`, {
      message,
      as,
    });

  /**
   * A person of the team answering the customer on WhatsApp, Messenger or
   * Instagram. Stored as a `human` message (never the agent's) and sent through
   * Meta inside its 24-hour window; the agent stops answering what was waiting.
   */
  static operatorMessage = async (
    clientId: string,
    sessionId: string,
    message: string,
  ): Promise<Session> =>
    api.post(`${base(clientId)}/sessions/${sessionId}/operator-message`, {
      message,
    });

  /**
   * The call's source recording. `fetchServer` rather than `api.get` — the
   * reply is a WAV, not JSON. SYSADMIN-only server-side.
   */
  static downloadRecording = async (
    clientId: string,
    sessionId: string,
  ): Promise<Blob> => {
    const response = await fetchServer(
      `${base(clientId)}/sessions/${sessionId}/recording`,
    );
    return response.blob();
  };

  /**
   * What a team member said on the phone — after a transfer, or a handset's own
   * call — as our Asterisk recorded it. Only exists when
   * `context.human_recording` is set. A WAV, hence `fetchServer`.
   */
  static downloadTeamRecording = async (
    clientId: string,
    sessionId: string,
  ): Promise<Blob> => {
    const response = await fetchServer(
      `${base(clientId)}/sessions/${sessionId}/human-recording`,
    );
    return response.blob();
  };

  /**
   * The whole conversation as a readable, silence-annotated CSV — the file a
   * slow reply gets dissected in. A file, so `fetchServer` again.
   */
  static exportCsv = async (
    clientId: string,
    sessionId: string,
  ): Promise<Blob> => {
    const response = await fetchServer(
      `${base(clientId)}/sessions/${sessionId}/export.csv`,
    );
    return response.blob();
  };
}
